/**
 * Reglages de verrouillage de cet appareil (« Vault timeout » de Bitwarden, « Auto-lock » de 1Password et de
 * Proton Pass, « Lock after » et « Clear clipboard » de KeePassXC) : delai d'auto-verrouillage, verrouillage a la
 * sortie de l'onglet, delai d'effacement du presse-papiers.
 *
 * Ce sont des preferences, jamais des secrets : elles vivent en clair dans `localStorage`. Elles se relisent donc
 * comme une donnee NON FIABLE : seuls les paliers proposes ci-dessous sont acceptes, toute autre valeur (absente,
 * trafiquee) retombe au defaut. Il n'existe volontairement ni « jamais », ni palier au-dela de 4 heures : un coffre
 * qui ne se referme plus, ou un mot de passe qui reste indefiniment dans le presse-papiers, n'est pas un reglage
 * qu'un coffre-fort zero-knowledge doit offrir.
 */

/** Delais d'auto-verrouillage proposes, en minutes d'inactivite. */
export const AUTO_LOCK_MINUTES_STEPS: readonly number[] = [1, 5, 15, 30, 60, 240];
export const AUTO_LOCK_MINUTES_DEFAULT = 5;

/** Delais d'effacement du presse-papiers proposes, en secondes. */
export const CLIPBOARD_CLEAR_SECONDS_STEPS: readonly number[] = [10, 20, 30, 60, 120, 300];
export const CLIPBOARD_CLEAR_SECONDS_DEFAULT = 30;

/** Cles `localStorage` : des preferences de cet appareil, jamais un secret. */
export const AUTO_LOCK_MINUTES_KEY = 'klavox:auto-lock-minutes';
export const CLIPBOARD_CLEAR_SECONDS_KEY = 'klavox:clipboard-clear-seconds';
export const LOCK_ON_HIDE_KEY = 'klavox:lock-on-hide';

/** Le palier d'auto-verrouillage retenu ; hors des paliers proposes, le defaut (5 minutes). */
export function autoLockMinutes(raw: string | null): number {
  const minutes = Number(raw);
  return AUTO_LOCK_MINUTES_STEPS.includes(minutes) ? minutes : AUTO_LOCK_MINUTES_DEFAULT;
}

/** Le palier d'effacement du presse-papiers retenu ; hors des paliers proposes, le defaut (30 secondes). */
export function clipboardClearSeconds(raw: string | null): number {
  const seconds = Number(raw);
  return CLIPBOARD_CLEAR_SECONDS_STEPS.includes(seconds) ? seconds : CLIPBOARD_CLEAR_SECONDS_DEFAULT;
}

/** Verrouiller des que l'onglet passe en arriere-plan : seulement si l'utilisateur l'a explicitement choisi. */
export function lockOnHide(raw: string | null): boolean {
  return raw === 'oui';
}

/** Libelle d'un delai d'auto-verrouillage (« 1 minute », « 15 minutes », « 4 heures »). */
export function autoLockLabel(minutes: number): string {
  if (minutes >= 60 && minutes % 60 === 0) return minutes === 60 ? '1 heure' : `${minutes / 60} heures`;
  return minutes === 1 ? '1 minute' : `${minutes} minutes`;
}

/** Libelle d'un delai d'effacement du presse-papiers (« 10 secondes », « 1 minute », « 5 minutes »). */
export function clipboardClearLabel(seconds: number): string {
  if (seconds >= 60 && seconds % 60 === 0) return seconds === 60 ? '1 minute' : `${seconds / 60} minutes`;
  return `${seconds} secondes`;
}
